import React, { useState, useEffect } from 'react';
import modalStyles from '../../../styles/AccountModal.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import { useUserDetailsApi } from '../../../hooks/useUserDetails';

export interface ChangePackageModalProps {
	currentSize: number | string;
	currentCost: number | string;
	minSize: number; // minimum allowed size (number of users)
	onClose: () => void;
	onSubmit: (newSize: number) => void;
}

const ChangePackageModal: React.FC<ChangePackageModalProps> = ({ currentSize, currentCost, minSize, onClose, onSubmit }) => {
	const { baselinePackageCost, perSeatCost, vatRate, fetchPackagePrices } = useUserDetailsApi();
	const [newSize, setNewSize] = useState<number>(Number(currentSize));
	const [showWarning, setShowWarning] = useState<boolean>(false);
	const newCost = baselinePackageCost + ((newSize - 1) * perSeatCost);
	const vatCost = newCost * vatRate;
	const totalCost = newCost + vatCost;

	useEffect(() => {
		fetchPackagePrices();
	}, [fetchPackagePrices]);

	React.useEffect(() => {
		setShowWarning(newSize < minSize);
	}, [newSize, minSize]);

	return (
		<div className={modalStyles.backdrop}>
			<div className={`${modalStyles.modal} ${modalStyles.modalSm}`}>
				<div className={modalStyles.header}>
					<h3 className={modalStyles.title}>Change Package Size</h3>
					<button
						type="button"
						className={modalStyles.closeButton}
						onClick={onClose}
						aria-label="Close"
					>
						×
					</button>
				</div>
				<div className={modalStyles.body}>
					<div className={modalStyles.grid} style={{ gridTemplateColumns: '1fr' }}>
						<div className={modalStyles.field}>
							<span className={modalStyles.label}>Current Seat Selection</span>
							<span className={modalStyles.staticValue}>{currentSize}</span>
						</div>
						<div className={modalStyles.field}>
							<span className={modalStyles.label}>Current Package Cost</span>
							<span className={modalStyles.staticValue}>R{currentCost}</span>
						</div>
						<div className={modalStyles.field}>
							<label className={modalStyles.label} htmlFor="newPackageSize">New Seat Selection</label>
							<input
								id="newPackageSize"
								type="number"
								min={1}
								value={newSize}
								onChange={e => setNewSize(Number(e.target.value))}
								className={modalStyles.input}
							/>
						</div>
						{showWarning && (
							<div className={modalStyles.warning}>
								Package size cannot be less than your current number of users ({minSize}).
							</div>
						)}
						<div className={modalStyles.field}>
							<span className={modalStyles.label}>New Package Cost</span>
							<span className={modalStyles.staticValue}>R{newCost}</span>
						</div>
						<div className={modalStyles.field}>
							<span className={modalStyles.label}>New Vat</span>
							<span className={modalStyles.staticValue}>R{vatCost}</span>
						</div>
						<div className={modalStyles.field}>
							<span className={modalStyles.label}>New Total</span>
							<span className={modalStyles.staticValue}>R{totalCost}</span>
						</div>
					</div>
				</div>
				<div className={modalStyles.footer}>
					<button
						className={`${buttonStyles.button} ${buttonStyles.secondary}`}
						onClick={onClose}
					>
						Cancel
					</button>
					<button
						className={`${buttonStyles.button} ${buttonStyles.primary}`}
						onClick={() => onSubmit(newSize)}
						disabled={showWarning}
					>
						Confirm
					</button>
				</div>
			</div>
		</div>
	);
};

export default ChangePackageModal;